import { createRoute, useNavigate } from '@tanstack/react-router'
import { projectRoute } from '../route'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core'
import {
  Plus,
  Upload,
  RefreshCw,
  PanelLeftClose,
  PanelLeftOpen,
  LayoutList,
  LayoutGrid,
  Search,
  Tags,
  Trash2,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { orpc } from '@/lib/orpc-client'
import { useAuthState } from '@/lib/auth-state'
import { useDateLocale } from '@/i18n/dates'
import { cn } from '@/lib/utils'
import { useDocumentUploads, UploadStatusPanel } from '@/components/document-upload'
import {
  DocumentTagSidebar,
  type DocumentTag,
  type TagFilter,
} from '@/components/document-tag-sidebar'
import {
  DocumentList,
  DocumentDragOverlay,
  type DocumentItem,
  type DocumentSort,
} from '@/components/document-list'

export interface WriteSearchParams {
  tag?: string
}

export const writeIndexRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/write/',
  component: DocumentListPage,
  validateSearch: (search: Record<string, unknown>): WriteSearchParams => ({
    tag: typeof search.tag === 'string' ? search.tag : undefined,
  }),
})

const MAX_TAGS = 5

/**
 * Snap the DragOverlay so its top-center aligns with the pointer.
 * Without this, grabbing a wide row from the far-right causes the
 * compact overlay card to render far from the cursor.
 */
const OVERLAY_CURSOR_LEFT = 24 // px from overlay left edge — aligns with file icon

const snapOverlayToPointer: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (!activatorEvent || !draggingNodeRect) return transform
  const event = activatorEvent as PointerEvent | MouseEvent
  if (typeof event.clientX !== 'number') return transform
  const offsetX = event.clientX - draggingNodeRect.left
  const offsetY = event.clientY - draggingNodeRect.top
  return {
    ...transform,
    x: transform.x + offsetX - OVERLAY_CURSOR_LEFT,
    y: transform.y + offsetY - 8,
  }
}

function DocumentListPage() {
  const { projectId } = writeIndexRoute.useParams()
  const navigate = useNavigate()
  const { me } = useAuthState()
  const { t } = useTranslation('documents')
  const { intl } = useDateLocale()

  const { tag: tagParam } = writeIndexRoute.useSearch()
  const selectedTag: TagFilter = tagParam === 'untagged' ? 'untagged' : (tagParam ?? 'all')

  const setSelectedTag = useCallback(
    (filter: TagFilter) => {
      void navigate({
        to: '/$projectId/write',
        params: { projectId },
        search: filter === 'all' ? {} : { tag: filter },
        replace: true,
      })
    },
    [navigate, projectId]
  )

  const [documents, setDocuments] = useState<DocumentItem[]>([])
  const [tags, setTags] = useState<DocumentTag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [isReindexing, setIsReindexing] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<DocumentSort>({ key: 'date', dir: 'desc' })
  const [deleteTarget, setDeleteTarget] = useState<DocumentItem | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [activeDragDoc, setActiveDragDoc] = useState<DocumentItem | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const lastClickedIdRef = useRef<string | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    try {
      const stored = localStorage.getItem('doc-view-mode')
      return stored === 'grid' ? 'grid' : 'list'
    } catch {
      return 'list'
    }
  })
  const handleSetViewMode = useCallback((mode: 'list' | 'grid') => {
    setViewMode(mode)
    try {
      localStorage.setItem('doc-view-mode', mode)
    } catch {
      /* ignore */
    }
  }, [])

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('doc-tag-sidebar-collapsed') === 'true'
    } catch {
      return false
    }
  })

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem('doc-tag-sidebar-collapsed', String(next))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const fetchTags = useCallback(async () => {
    try {
      const data = await orpc.documentTags.list({ projectId })
      setTags(data.items)
    } catch {
      /* ignore */
    }
  }, [projectId])

  const fetchDocuments = useCallback(async () => {
    try {
      const params: { projectId: string; tagId?: string } = { projectId }
      if (selectedTag === 'untagged') {
        params.tagId = '__untagged__'
      } else if (selectedTag !== 'all') {
        params.tagId = selectedTag
      }
      const data = await orpc.documents.list(params)
      setDocuments(data.items)
    } catch {
      /* ignore */
    }
  }, [projectId, selectedTag])

  // Uploads: header button opens the native picker; dropping files from the
  // OS onto the list area uploads too (drop overlay below).
  const uploads = useDocumentUploads(projectId, fetchDocuments)
  const [dropActive, setDropActive] = useState(false)
  const dragDepthRef = useRef(0)

  useEffect(() => {
    setIsLoading(true)
    Promise.all([fetchTags(), fetchDocuments()]).finally(() => setIsLoading(false))
  }, [fetchTags, fetchDocuments])

  // Stale tag fallback — if URL has a tag ID that doesn't exist, reset to 'all'
  useEffect(() => {
    if (
      tags.length > 0 &&
      selectedTag !== 'all' &&
      selectedTag !== 'untagged' &&
      !tags.some((t) => t.id === selectedTag)
    ) {
      setSelectedTag('all')
    }
  }, [tags, selectedTag, setSelectedTag])

  // Search + sort applied client-side (the list is capped at 200 docs).
  const visibleDocuments = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const filtered = needle
      ? documents.filter((d) => d.title.toLowerCase().includes(needle))
      : [...documents]
    const dir = sort.dir === 'asc' ? 1 : -1
    filtered.sort((a, b) => {
      if (sort.key === 'name') return a.title.localeCompare(b.title, intl) * dir
      if (sort.key === 'size') return ((a.sizeBytes ?? -1) - (b.sizeBytes ?? -1)) * dir
      return a.updatedAt.localeCompare(b.updatedAt) * dir
    })
    return filtered
  }, [documents, search, sort, intl])

  const visibleIds = useMemo(() => visibleDocuments.map((d) => d.id), [visibleDocuments])

  // Clear selection when the underlying set of rows changes meaning
  useEffect(() => {
    setSelectedIds(new Set())
    lastClickedIdRef.current = null
  }, [selectedTag, search])

  // Escape clears selection
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedIds(new Set())
        lastClickedIdRef.current = null
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const handleToggleSelect = useCallback(
    (docId: string, shiftKey: boolean) => {
      if (shiftKey && lastClickedIdRef.current) {
        const from = visibleIds.indexOf(lastClickedIdRef.current)
        const to = visibleIds.indexOf(docId)
        if (from !== -1 && to !== -1) {
          const [start, end] = from < to ? [from, to] : [to, from]
          const range = visibleIds.slice(start, end + 1)
          setSelectedIds((prev) => {
            const next = new Set(prev)
            for (const id of range) next.add(id)
            return next
          })
          lastClickedIdRef.current = docId
          return
        }
      }
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(docId)) next.delete(docId)
        else next.add(docId)
        return next
      })
      lastClickedIdRef.current = docId
    },
    [visibleIds]
  )

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id))
      return allSelected ? new Set() : new Set(visibleIds)
    })
    lastClickedIdRef.current = null
  }, [visibleIds])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
    lastClickedIdRef.current = null
  }, [])

  // DnD (drag rows onto tag sidebar)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  const [dragSelectedCount, setDragSelectedCount] = useState(1)

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const doc = event.active.data.current?.document as DocumentItem | undefined
      if (!doc) {
        setActiveDragDoc(null)
        return
      }
      if (selectedIds.has(doc.id)) {
        setDragSelectedCount(selectedIds.size)
      } else {
        clearSelection()
        setDragSelectedCount(1)
      }
      setActiveDragDoc(doc)
    },
    [selectedIds, clearSelection]
  )

  const addTagToDocuments = useCallback(
    async (docIds: string[], targetTagId: string) => {
      const updates = docIds.map(async (id) => {
        const d = documents.find((x) => x.id === id)
        if (!d) return
        const currentTagIds = d.tags.map((tag) => tag.id)
        let newTagIds: string[]
        if (targetTagId === '__untagged__') {
          newTagIds = []
        } else if (currentTagIds.includes(targetTagId) || currentTagIds.length >= MAX_TAGS) {
          return // skip — already tagged or at limit
        } else {
          newTagIds = [...currentTagIds, targetTagId]
        }
        await orpc.documents.setTags({ projectId, documentId: id, tagIds: newTagIds })
      })
      await Promise.all(updates)
    },
    [documents, projectId]
  )

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveDragDoc(null)
      setDragSelectedCount(1)
      const { active, over } = event
      if (!over) return
      const doc = active.data.current?.document as DocumentItem | undefined
      if (!doc) return
      const draggedIds = selectedIds.has(doc.id) ? [...selectedIds] : [doc.id]
      try {
        await addTagToDocuments(draggedIds, over.id as string)
      } catch {
        /* ignore */
      }
      clearSelection()
      await fetchDocuments()
    },
    [selectedIds, addTagToDocuments, clearSelection, fetchDocuments]
  )

  const handleBulkAddTag = useCallback(
    async (tagId: string) => {
      try {
        await addTagToDocuments([...selectedIds], tagId)
      } catch {
        /* ignore */
      }
      clearSelection()
      await fetchDocuments()
    },
    [selectedIds, addTagToDocuments, clearSelection, fetchDocuments]
  )

  const handleBulkDelete = useCallback(async () => {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      for (const id of selectedIds) {
        await orpc.documents.delete({ projectId, documentId: id })
      }
    } catch {
      /* ignore */
    } finally {
      setIsDeleting(false)
      setBulkDeleteOpen(false)
      clearSelection()
      await fetchDocuments()
    }
  }, [isDeleting, selectedIds, projectId, clearSelection, fetchDocuments])

  // Poll for conversion status updates while any document is pending/converting
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    const hasConverting = documents.some(
      (d) => d.conversionStatus === 'pending' || d.conversionStatus === 'converting'
    )
    if (hasConverting && !pollRef.current) {
      pollRef.current = setInterval(() => {
        void fetchDocuments()
      }, 3000)
    } else if (!hasConverting && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [documents, fetchDocuments])

  const handleCreate = async () => {
    if (isCreating) return
    setIsCreating(true)
    try {
      const doc = await orpc.documents.create({ projectId, title: t('list.newDocument') })
      void navigate({ to: '/$projectId/write/$docId', params: { projectId, docId: doc.id } })
    } catch {
      setIsCreating(false)
    }
  }

  const handleReindex = async () => {
    if (isReindexing) return
    setIsReindexing(true)
    try {
      await orpc.documents.reindex({ projectId })
      await fetchDocuments()
    } catch {
      /* ignore */
    } finally {
      setIsReindexing(false)
    }
  }

  const handleRegenerateMarkdown = async () => {
    if (isRegenerating) return
    setIsRegenerating(true)
    try {
      await orpc.documents.regenerateMarkdown({ projectId })
      await fetchDocuments()
    } catch {
      /* ignore */
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleDeleteDocument = async () => {
    if (!deleteTarget || isDeleting) return
    setIsDeleting(true)
    try {
      await orpc.documents.delete({ projectId, documentId: deleteTarget.id })
      setDeleteTarget(null)
      await fetchDocuments()
    } catch {
      /* ignore */
    } finally {
      setIsDeleting(false)
    }
  }

  const handleReindexDocument = async (doc: DocumentItem) => {
    try {
      await orpc.documents.reindexOne({ projectId, documentId: doc.id })
      await fetchDocuments()
    } catch {
      /* ignore */
    }
  }

  const handleCreateTag = async (name: string, color: string) => {
    try {
      await orpc.documentTags.create({ projectId, name, color })
      await fetchTags()
    } catch {
      /* ignore */
    }
  }

  const handleUpdateTag = async (tagId: string, updates: { name?: string; color?: string }) => {
    try {
      await orpc.documentTags.update({ projectId, tagId, ...updates })
      await fetchTags()
      await fetchDocuments()
    } catch {
      /* ignore */
    }
  }

  const handleDeleteTag = async (tagId: string) => {
    try {
      await orpc.documentTags.delete({ projectId, tagId })
      if (selectedTag === tagId) setSelectedTag('all')
      await fetchTags()
      await fetchDocuments()
    } catch {
      /* ignore */
    }
  }

  const handleSetTags = async (docId: string, tagIds: string[]) => {
    try {
      await orpc.documents.setTags({ projectId, documentId: docId, tagIds })
      await fetchDocuments()
    } catch {
      /* ignore */
    }
  }

  // OS file drag → drop overlay (dnd-kit drags use pointer events, never these)
  const isFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes('Files')

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {uploads.input}

      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleSetViewMode('list')}
              className="rounded-r-none border-r border-border px-2"
              title={t('list.listView')}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleSetViewMode('grid')}
              className="rounded-l-none px-2"
              title={t('list.gridView')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {me?.user.email === 'andreas@hubsystem.se' && (
            <>
              <Button
                variant="outline"
                onClick={() => void handleReindex()}
                disabled={isReindexing}
                className="gap-2"
              >
                <RefreshCw className={cn('h-4 w-4', isReindexing && 'animate-spin')} />
                {t('list.reindex')}
              </Button>
              <Button
                variant="outline"
                onClick={() => void handleRegenerateMarkdown()}
                disabled={isRegenerating}
                className="gap-2"
              >
                <RefreshCw className={cn('h-4 w-4', isRegenerating && 'animate-spin')} />
                {t('list.regenerateMarkdown')}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={uploads.openPicker} className="gap-2">
            <Upload className="h-4 w-4" />
            {t('list.upload')}
          </Button>
          <Button onClick={handleCreate} disabled={isCreating} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('list.newDocument')}
          </Button>
        </div>
      </div>

      {/* Two-panel body with DnD */}
      <DndContext
        sensors={sensors}
        modifiers={[snapOverlayToPointer]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-1 overflow-hidden">
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center border-r border-border px-1 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSidebar}
                className="h-8 w-8 p-0"
                title={t('list.showTags')}
              >
                <PanelLeftOpen className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col border-r border-border">
              <div className="flex items-center justify-end px-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className="h-7 w-7 p-0 text-muted-foreground"
                  title={t('list.hideTags')}
                >
                  <PanelLeftClose className="h-3.5 w-3.5" />
                </Button>
              </div>
              <DocumentTagSidebar
                tags={tags}
                selectedTag={selectedTag}
                onSelectTag={setSelectedTag}
                onCreateTag={handleCreateTag}
                onUpdateTag={handleUpdateTag}
                onDeleteTag={handleDeleteTag}
              />
            </div>
          )}

          {/* File area: search / bulk bar + table, with an OS-drag drop overlay */}
          <div
            className="relative flex min-w-0 flex-1 flex-col"
            onDragEnter={(e) => {
              if (!isFileDrag(e)) return
              e.preventDefault()
              dragDepthRef.current++
              setDropActive(true)
            }}
            onDragOver={(e) => {
              if (isFileDrag(e)) e.preventDefault()
            }}
            onDragLeave={(e) => {
              if (!isFileDrag(e)) return
              dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
              if (dragDepthRef.current === 0) setDropActive(false)
            }}
            onDrop={(e) => {
              if (!isFileDrag(e)) return
              e.preventDefault()
              dragDepthRef.current = 0
              setDropActive(false)
              if (e.dataTransfer.files.length) void uploads.uploadFiles(e.dataTransfer.files)
            }}
          >
            {selectedIds.size > 0 ? (
              <div className="flex items-center gap-2 border-b border-border px-4 py-2">
                <span className="text-sm font-medium">
                  {t('list.selectedCount', { count: selectedIds.size })}
                </span>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Tags className="h-3.5 w-3.5" />
                      {t('list.addToTag')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-44">
                    {tags.length === 0 && (
                      <DropdownMenuItem disabled>
                        <span className="text-xs text-muted-foreground">{t('list.noTagsYet')}</span>
                      </DropdownMenuItem>
                    )}
                    {tags.map((tag) => (
                      <DropdownMenuItem key={tag.id} onClick={() => void handleBulkAddTag(tag.id)}>
                        <span
                          className="mr-2 h-2.5 w-2.5 flex-shrink-0 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="gap-2 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('list.deleteSelected')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="ml-auto gap-1 text-muted-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                  {t('list.clearSelection')}
                </Button>
              </div>
            ) : (
              <div className="border-b border-border px-4 py-2">
                <div className="relative">
                  <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('list.searchPlaceholder')}
                    className="w-full rounded-lg border border-border bg-transparent py-2 pr-3 pl-9 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-muted-foreground/40"
                  />
                </div>
              </div>
            )}

            <DocumentList
              documents={visibleDocuments}
              allTags={tags}
              projectId={projectId}
              sort={sort}
              onSortChange={setSort}
              viewMode={viewMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              isLoading={isLoading}
              hasSearch={search.trim().length > 0}
              onSetTags={handleSetTags}
              onDeleteDocument={(doc) => setDeleteTarget(doc)}
              onReindexDocument={
                me?.user.email === 'andreas@hubsystem.se' ? handleReindexDocument : undefined
              }
              onCreateDocument={handleCreate}
              onOpenPicker={uploads.openPicker}
            />

            {dropActive && (
              <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-background/80 backdrop-blur-[2px]">
                <div className="text-center">
                  <Upload className="mx-auto mb-2 h-8 w-8 text-foreground" />
                  <p className="text-sm font-medium">{t('list.dropOverlay')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t(uploads.transcription ? 'upload.hintWithAudio' : 'upload.hint')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDragDoc ? (
            <DocumentDragOverlay document={activeDragDoc} count={dragSelectedCount} />
          ) : null}
        </DragOverlay>
      </DndContext>

      <UploadStatusPanel
        items={uploads.items}
        isUploading={uploads.isUploading}
        onDismiss={uploads.clear}
      />

      {/* Delete single document */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('list.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('list.deleteDialog.description', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              {t('list.deleteDialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteDocument()}
              disabled={isDeleting}
            >
              {isDeleting ? t('list.deleteDialog.deleting') : t('list.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(open) => !open && setBulkDeleteOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('list.bulkDeleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('list.bulkDeleteDialog.description', { count: selectedIds.size })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={isDeleting}
            >
              {t('list.deleteDialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleBulkDelete()}
              disabled={isDeleting}
            >
              {isDeleting ? t('list.deleteDialog.deleting') : t('list.deleteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
